import {
  Box,
  Button,
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Badge,
  IconButton,
} from "@chakra-ui/react";
import { ChevronRightIcon } from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";
import { customerDashboardStyles } from "theme/overrides/customer";
import { pad_with_zeroes } from "network/helper";
import AppSpinner from "components/spinner/AppSpinner";
import { getOrderStatusInfo } from "constants/statuses";

interface LatestUpdatesCardProps {
  boxShadow: string;
  isFetching: boolean;
  lastUpdatedOrders: any[] | null;
  onViewAll: () => void;
  onProjectClick?: (orderId: string) => void;
}

const isDraftStatus = (statusId: string | number | null): boolean => {
  const id = String(statusId);
  return id === "1" || id === "10";
};

export const LatestUpdatesCard: React.FC<LatestUpdatesCardProps> = ({
  isFetching,
  lastUpdatedOrders,
  onViewAll,
  onProjectClick,
}) => {
  const navigate = useNavigate();
  console.log("LatestUpdatesCard props:", {
    isFetching,
    lastUpdatedOrders,
    onViewAll,
    onProjectClick,
  });

  const handleRowClick = (order: any) => {
    const orderId = order?.order_id;
    const statusId = order?.order_status_id;

    if (isDraftStatus(statusId)) {
      navigate(`/customer/main/${orderId}`);
    } else {
      navigate(`/customer/project/${orderId}`);
    }
  };
  return (
    <Box
      padding={customerDashboardStyles.card.padding}
      borderRadius={customerDashboardStyles.card.borderRadius}
      border="1px solid #E6ECF3"
      height="100%"
      bg="#F5F7FA"
    >
      <Text fontSize="20px" fontWeight="600" mb="24px" color="#000000">
        Recent Orders
      </Text>
      <Box maxW={{ md: "unset", base: "calc(100vw - 100px)" }} overflow="auto">
        <Table fontSize="14px">
          <Thead>
            <Tr>
              <Th
                padding="12px 16px"
                fontSize="14px"
                fontWeight="600"
                color="#0B0E10"
                textTransform="none"
                borderBottom="1px solid #E5E7EB"
              >
                Project #
              </Th>
              <Th
                padding="12px 16px"
                fontSize="14px"
                fontWeight="600"
                color="#0B0E10"
                textTransform="none"
                borderBottom="1px solid #E5E7EB"
              >
                Status
              </Th>
              <Th
                padding="12px 16px"
                fontSize="14px"
                fontWeight="600"
                color="#0B0E10"
                textTransform="none"
                borderBottom="1px solid #E5E7EB"
              >
                Type
              </Th>
              <Th
                padding="12px 16px"
                fontSize="14px"
                fontWeight="600"
                color="#0B0E10"
                textTransform="none"
                borderBottom="1px solid #E5E7EB"
              >
                Due Date
              </Th>
              <Th
                padding="12px 16px"
                fontSize="14px"
                fontWeight="600"
                color="#0B0E10"
                textTransform="none"
                borderBottom="1px solid #E5E7EB"
              >
                Price
              </Th>
              <Th
                padding="12px 16px"
                borderBottom="1px solid #E5E7EB"
                width="50px"
              />
            </Tr>
          </Thead>
          <Tbody>
            {!isFetching &&
              (lastUpdatedOrders?.length ?? 0) > 0 &&
              lastUpdatedOrders?.map((it: any, index: number) => (
                <Tr
                  key={index}
                  cursor="pointer"
                  onClick={() => handleRowClick(it)}
                >
                  <Td
                    padding="12px"
                    fontSize="16px"
                    color="#111827"
                    borderBottom="1px solid #E6ECF3"
                  >
                    #{pad_with_zeroes(it?.order_id, 4)}
                  </Td>
                  <Td padding="12px" borderBottom="1px solid #E6ECF3">
                    <Badge
                      bg={getOrderStatusInfo(it?.order_status_id).bg}
                      color={getOrderStatusInfo(it?.order_status_id).text}
                      borderRadius="4px"
                      px="5px 8px"
                      fontSize="16px"
                      fontWeight="400"
                      textTransform="none"
                      w="70px"
                      display="flex"
                      justifyContent="center"
                    >
                      {getOrderStatusInfo(it?.order_status_id).name}
                    </Badge>
                  </Td>
                  <Td
                    padding="12px"
                    fontSize="16px"
                    color="#0B0E10"
                    borderBottom="1px solid #E6ECF3"
                  >
                    {it?.translation_type
                      ? it.translation_type.charAt(0).toUpperCase() +
                        it.translation_type.slice(1)
                      : "-"}
                  </Td>
                  <Td
                    padding="12px"
                    fontSize="16px"
                    fontWeight="400"
                    color="#0B0E10"
                    borderBottom="1px solid #E6ECF3"
                  >
                    {it?.order_due_at
                      ? (() => {
                          const d = new Date(it.order_due_at);
                          return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
                        })()
                      : "-"}
                  </Td>
                  <Td
                    padding="12px"
                    fontSize="16px"
                    color="#0B0E10"
                    fontWeight="400"
                    borderBottom="1px solid #E6ECF3"
                  >
                    ${it?.order_total || "0.00"}
                  </Td>
                  <Td
                    padding="12px"
                    borderBottom="1px solid #E6ECF3"
                    textAlign="right"
                  >
                    <IconButton
                      aria-label="View details"
                      icon={<ChevronRightIcon w="24px" h="24px" />}
                      variant="ghost"
                      size="sm"
                      _hover={{ bg: "transparent" }}
                    />
                  </Td>
                </Tr>
              ))}
            {isFetching && (
              <Tr>
                <Td colSpan={6} padding="24px">
                  <Flex justify="center" align="center">
                    <AppSpinner />
                  </Flex>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>
      <Flex mt="16px" justifyContent="center">
        <Button
          type="button"
          variant="outline"
          width="200px"
          height="40px"
          borderColor="#4C6FFF"
          color="#4C6FFF"
          fontSize="14px"
          fontWeight="600"
          borderRadius="24px"
          onClick={onViewAll}
          _hover={{
            bg: "#4C6FFF",
            color: "#FFFFFF",
          }}
        >
          View All
        </Button>
      </Flex>
    </Box>
  );
};

export default LatestUpdatesCard;
